import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { Meta } from 'src/components/Meta';
import { StickyCard } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import {
  fetchPlatformSmsDetails,
  resendPlatformOtp,
  updatePlatformSmsDetails,
} from 'src/models/platform/platform';
import {
  is10DLCApproved,
  isAwaiting10DlcBrandApproval,
  isAwaiting10DlcCampaignApproval,
  isAwaiting10DlcNumberApproval,
} from 'src/models/platform/smsUtils';
import toast from 'src/utils/toast';

import { SMSSetupForm, SMSSetupFormValues } from './SMSSetupForm';

export const SMSSetup = ({
  platform,
  onboarding,
  updateOnboarding,
  updatePlatform,
  trainer,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.sms' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  const platformId = platform?.id;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showOptOutDialog, setShowOptOutDialog] = useState(false);
  const [isRetryingAfterError, setIsRetryingAfterError] = useState(false);

  const {
    data: smsDetails,
    isLoading: isLoadingSms,
    refetch: refetchSmsDetails,
  } = useQuery({
    queryKey: ['platformSms', platformId],
    queryFn: () => fetchPlatformSmsDetails({ id: platformId }),
    enabled: !!platformId,
  });

  const errorType = smsDetails?.twilio10dlcError?.type;
  const hasEinError = errorType === 'ein_mismatch' || errorType === 'invalid_ein';
  const hasOtpPending = errorType === 'otp_pending';
  const daysRemaining = smsDetails?.daysUntilSmsDisabled;

  // Check if the form has already been submitted by checking if twilio10DlcData has data
  React.useEffect(() => {
    if (onboarding?.twilio10DlcData) {
      // Check if there's actual data in the object (not just an empty object)
      const hasData = Object.values(onboarding.twilio10DlcData).some(
        (value) => value !== null && value !== undefined && value !== ''
      );

      if (hasData) {
        setIsSubmitted(true);
      }
    }
  }, [onboarding?.twilio10DlcData]);

  const updateOnboardingMutation = useMutation({
    mutationFn: async (values: SMSSetupFormValues) => {
      // First save the onboarding data
      await updateOnboarding({
        twilio10DlcData: {
          companyName: values.businessName,
          website: values.websiteUrl,
          businessType: values.businessType,
          businessIdType: values.businessIdType,
          businessId: values.businessId,
          regions: values.regionsOfOperation.join(','),
          jobTitle: values.jobTitle,
          jobPosition: values.jobPosition,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phoneNumber,
          address: values.streetAddress,
          address2: values.streetAddress2,
          city: values.city,
          state: values.state,
          zip: values.postalCode,
          country: values.country,
          areaCode: values.areaCode,
          areaCodeTwo: values.areaCodeTwo,
        },
      });

      // If retrying after an error, trigger 10DLC initiation
      if (isRetryingAfterError && platformId) {
        await updatePlatformSmsDetails(platformId, '');
      }
    },

    onSuccess: () => {
      toast.success(t('success.saved'));
      setIsSubmitted(true);
      setIsRetryingAfterError(false);
      refetchSmsDetails();
    },

    onError: () => {
      toast.error(t('errors.failedSave'));
    },
  });

  const optOutOfSmsMutation = useMutation({
    mutationFn: () =>
      updatePlatform({
        twilio10dlcExcluded: true,
      }),

    onSuccess: () => {
      toast.success(t('success.optedOut', 'Successfully opted out of SMS'));
      refetchSmsDetails();
    },

    onError: () => {
      toast.error(t('errors.failedOptOut'));
    },

    onSettled: () => {
      setShowOptOutDialog(false);
    },
  });

  const optInToSmsMutation = useMutation({
    mutationFn: () =>
      updatePlatform({
        twilio10dlcExcluded: false,
      }),

    onSuccess: () => {
      toast.success(t('success.enabled'));
      refetchSmsDetails();
    },

    onError: () => {
      toast.error(t('errors.failedToEnable'));
    },
  });

  const resendOtpMutation = useMutation({
    mutationFn: () => resendPlatformOtp(platformId),

    onSuccess: () => {
      toast.success(t('otpPending.resendSuccess'));
      refetchSmsDetails();
    },

    onError: () => {
      toast.error(t('otpPending.resendError'));
    },
  });

  const handleSubmit = async (values: SMSSetupFormValues) => {
    await updateOnboardingMutation.mutate(values);
  };

  const handleOptOut = () => {
    setShowOptOutDialog(true);
  };

  const confirmOptOut = () => {
    optOutOfSmsMutation.mutate();
  };

  const handleOptIn = () => {
    optInToSmsMutation.mutate();
  };

  const getInitialValues = (): SMSSetupFormValues => {
    const data = onboarding?.twilio10DlcData || {};

    // Determine the website URL based on available data
    let websiteUrl = data.website || platform?.businessProfile?.url || '';

    // If a subdomain is set, use it to create a full Exercise.com URL
    if (platform?.subdomain) {
      websiteUrl = `https://${platform.subdomain}.exercise.com`;
    }

    // If a custom domain is set, use it instead
    // We don't have direct access to domain verification status in the Platform type,
    // but if a host is set, it's likely verified and in use
    if (platform?.host) {
      websiteUrl = `https://${platform.host}`;
    }

    return {
      businessName: data.companyName || platform?.businessProfile?.name || '',
      websiteUrl,
      businessType: data.businessType || '',
      businessIdType: data.businessIdType || '',
      businessId: data.businessId || '',
      regionsOfOperation: data.regions ? data.regions.split(',') : [],
      jobTitle: data.jobTitle || '',
      jobPosition: data.jobPosition || '',
      firstName: data.firstName || trainer?.firstName || '',
      lastName: data.lastName || trainer?.lastName || '',
      email: data.email || platform?.businessProfile?.supportEmail || '',
      phoneNumber: data.phone || platform?.businessProfile?.supportPhone || '',
      streetAddress:
        data.address || platform?.businessProfile?.supportAddress?.line1 || '',
      streetAddress2:
        data.address2 || platform?.businessProfile?.supportAddress?.line2 || '',
      city: data.city || platform?.businessProfile?.supportAddress?.city || '',
      state:
        data.state || platform?.businessProfile?.supportAddress?.state || '',
      postalCode:
        data.zip || platform?.businessProfile?.supportAddress?.postalCode || '',
      country:
        data.country ||
        platform?.businessProfile?.supportAddress?.country ||
        'US',
      areaCode: data.areaCode || '',
      areaCodeTwo: data.areaCodeTwo || '',
    };
  };

  if (isLoadingSms) {
    return null;
  }

  const isSMSComplete = smsDetails && is10DLCApproved(smsDetails);
  const isSmsExcluded = platform?.twilio10dlcExcluded;

  const isSmsDecisionNeeded =
    isSmsExcluded === undefined || isSmsExcluded === null;

  const getSetupStatus = () => {
    if (!smsDetails) return null;

    if (isAwaiting10DlcBrandApproval(smsDetails)) {
      return {
        message: t('status.awaitingBrandApproval'),
        icon: 'hourglass-half',
      };
    }

    if (isAwaiting10DlcCampaignApproval(smsDetails)) {
      return {
        message: t('status.awaitingCampaignApproval'),
        icon: 'hourglass-half',
      };
    }

    if (isAwaiting10DlcNumberApproval(smsDetails)) {
      return {
        message: t('status.awaitingNumberApproval'),
        icon: 'hourglass-half',
      };
    }

    return null;
  };

  const setupStatus = getSetupStatus();

  const renderSmsDecisionScreen = () => {
    return (
      <>
        <StickyCard
          title={t('decision.title')}
          subtitle={t('decision.subtitle')}
        >
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Icon
              icon={icon({ name: 'comment-dots', style: 'regular' })}
              sx={{ fontSize: 64, mb: 3, color: 'primary.main' }}
            />
            <Typography variant="h5" sx={{ mb: 3 }}>
              {t('decision.yesEnableQuestion')}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
            >
              {t('decision.message')}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleOptOut}
                disabled={optOutOfSmsMutation.isPending}
                size="large"
              >
                {t('decision.noOptOut')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOptIn}
                disabled={optInToSmsMutation.isPending}
                size="large"
              >
                {t('decision.yesEnable')}
              </Button>
            </Box>
          </Box>
        </StickyCard>
        <ConfirmDialog
          open={showOptOutDialog}
          title={t('optOutConfirm.title')}
          actions={[
            {
              label: tCommon('buttons.cancel'),
              onClick: () => setShowOptOutDialog(false),
              color: 'secondary',
            },
            {
              label: tCommon('buttons.optOut'),
              onClick: confirmOptOut,
              color: 'error',
              variant: 'contained',
              disabled: optOutOfSmsMutation.isPending,
            },
          ]}
        >
          {t('optOutConfirm.message')}
        </ConfirmDialog>
      </>
    );
  };

  const getContent = () => {
    // If we need to ask the user whether they want SMS
    if (isSmsDecisionNeeded) {
      return renderSmsDecisionScreen();
    }

    // If the user has opted out of SMS
    if (isSmsExcluded) {
      return (
        <StickyCard
          title={t('optedOut.title')}
          subtitle={t('optedOut.subtitle')}
        >
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Icon
              icon={icon({ name: 'ban', style: 'solid' })}
              sx={{ fontSize: 64, mb: 2, color: 'text.secondary' }}
            />
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t('optedOut.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              {t('optedOut.message')} {t('optedOut.contactInfo')}
            </Typography>
          </Box>
        </StickyCard>
      );
    }

    // If SMS setup is complete
    if (isSMSComplete) {
      return (
        <StickyCard
          title={t('completed.title')}
          subtitle={t('completed.subtitle')}
        >
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Icon
              icon={icon({ name: 'check-circle', style: 'regular' })}
              sx={{ fontSize: 64, mb: 2, color: 'success.main' }}
            />
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t('completed.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('completed.message')}
            </Typography>
            <Typography variant="h6">
              {t('completed.smsNumber', {
                number: platform?.twilioPhoneNumber,
              })}
            </Typography>
          </Box>
        </StickyCard>
      );
    }

    // If there's an EIN error and user wants to retry, show the form
    if (hasEinError && !isSubmitted) {
      return (
        <SMSSetupForm
          initialValues={getInitialValues()}
          onSubmit={handleSubmit}
          isSubmitting={updateOnboardingMutation.isPending}
          submitButtonText={tCommon('buttons.saveChanges')}
          title={t('setup.title')}
          subtitle={t('setup.subtitle')}
        />
      );
    }

    // If SMS setup is in progress
    if (setupStatus) {
      return (
        <StickyCard title={t('status.title')} subtitle={t('status.subtitle')}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Icon
              icon={icon({ name: 'hourglass-half', style: 'regular' })}
              sx={{ fontSize: 64, mb: 2, color: 'info.main' }}
            />
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t('status.title')}
            </Typography>
            <Typography color="text.secondary">
              {setupStatus.message}
            </Typography>
          </Box>
        </StickyCard>
      );
    }

    // If the form has been submitted
    if (isSubmitted) {
      return (
        <StickyCard title={t('setup.title')} subtitle={t('setup.thankyou')}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Icon
              icon={icon({ name: 'check-circle', style: 'regular' })}
              sx={{ fontSize: 64, mb: 2, color: 'success.main' }}
            />
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t('setup.thankyou')}
            </Typography>
            <Typography color="text.secondary">
              {t('setup.thankyouMessage')}
            </Typography>
          </Box>
        </StickyCard>
      );
    }

    // Return the SMS setup form
    return (
      <SMSSetupForm
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
        isSubmitting={updateOnboardingMutation.isPending}
        submitButtonText={tCommon('buttons.saveChanges')}
        title={t('setup.title')}
        subtitle={t('setup.subtitle')}
      />
    );
  };

  // Get error type display text
  const getErrorTypeDisplay = (errorType: string | undefined) => {
    if (!errorType) return '';
    return t(`einError.types.${errorType}`, t('einError.types.default'));
  };

  // Handle reset to allow user to edit and resubmit
  const handleResetAndRetry = () => {
    setIsRetryingAfterError(true);
    setIsSubmitted(false);
  };

  // Render the EIN error warning banner
  const renderEinErrorBanner = () => {
    // Hide banner if no error, or if user is actively editing the form
    if (!hasEinError || !isSubmitted) return null;

    const isUrgent = daysRemaining != null && daysRemaining <= 3;

    return (
      <Alert
        severity={isUrgent ? 'error' : 'warning'}
        icon={
          <Icon icon={icon({ name: 'exclamation-triangle', style: 'solid' })} />
        }
        sx={{
          mb: 3,
          '& .MuiAlert-message': {
            color: 'text.primary',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('einError.title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {getErrorTypeDisplay(smsDetails?.twilio10dlcError?.type)}
        </Typography>
        {daysRemaining != null && (
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {daysRemaining > 0
              ? t('einError.daysRemaining', { count: daysRemaining })
              : t('einError.deadline')}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('einError.instructions')}
        </Typography>
        <Button
          variant="contained"
          color={isUrgent ? 'error' : 'primary'}
          size="small"
          onClick={handleResetAndRetry}
          startIcon={
            <Icon icon={icon({ name: 'rotate-right', style: 'solid' })} />
          }
        >
          {t('einError.updateAndRetry')}
        </Button>
      </Alert>
    );
  };

  // Render the OTP pending warning banner
  const renderOtpPendingBanner = () => {
    if (!hasOtpPending) return null;

    const isUrgent = daysRemaining != null && daysRemaining <= 3;

    return (
      <Alert
        severity={isUrgent ? 'error' : 'warning'}
        icon={
          <Icon icon={icon({ name: 'mobile-alt', style: 'solid' })} />
        }
        sx={{
          mb: 3,
          '& .MuiAlert-message': {
            color: 'text.primary',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('otpPending.title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('otpPending.description')}
        </Typography>
        {daysRemaining != null && (
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {daysRemaining > 0
              ? t('otpPending.daysRemaining', { count: daysRemaining })
              : t('otpPending.deadline')}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('otpPending.instructions')}
        </Typography>
        <Button
          variant="contained"
          color={isUrgent ? 'error' : 'primary'}
          size="small"
          onClick={() => resendOtpMutation.mutate()}
          disabled={resendOtpMutation.isPending}
          startIcon={
            <Icon icon={icon({ name: 'paper-plane', style: 'solid' })} />
          }
        >
          {resendOtpMutation.isPending
            ? t('otpPending.resending')
            : t('otpPending.resendButton')}
        </Button>
      </Alert>
    );
  };

  return (
    <>
      <Meta title={t('setup.title')} />
      {renderEinErrorBanner()}
      {renderOtpPendingBanner()}
      {getContent()}
    </>
  );
};
